import { TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Bell, ArrowRight, BarChart3, DollarSign } from 'lucide-react';

export function DashboardMockup() {
  const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun'];
  const values = [42, 38, 55, 48, 61, 58];
  const maxVal = Math.max(...values);

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden w-full max-w-xl">
      <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex items-center gap-2">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-red-400" />
          <div className="w-3 h-3 rounded-full bg-yellow-400" />
          <div className="w-3 h-3 rounded-full bg-green-400" />
        </div>
        <span className="text-xs text-gray-500 ml-2 font-medium">TrezoOne — Tableau de bord</span>
      </div>
      <div className="p-5">
        <div className="grid grid-cols-3 gap-3 mb-5">
          <div className="bg-blue-50 rounded-xl p-3">
            <div className="text-xs text-blue-600 font-medium mb-1">Trésorerie</div>
            <div className="text-xl font-black text-blue-700">58 420 €</div>
            <div className="flex items-center gap-1 mt-1 text-xs text-green-600">
              <TrendingUp size={11} /> +12%
            </div>
          </div>
          <div className="bg-green-50 rounded-xl p-3">
            <div className="text-xs text-green-600 font-medium mb-1">Encaissements</div>
            <div className="text-xl font-black text-green-700">12 800 €</div>
            <div className="text-xs text-gray-400 mt-1">ce mois</div>
          </div>
          <div className="bg-red-50 rounded-xl p-3">
            <div className="text-xs text-red-600 font-medium mb-1">Décaissements</div>
            <div className="text-xl font-black text-red-700">8 340 €</div>
            <div className="flex items-center gap-1 mt-1 text-xs text-red-500">
              <TrendingDown size={11} /> -3%
            </div>
          </div>
        </div>
        <div className="mb-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-gray-700">Évolution de la trésorerie</span>
            <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">6 mois</span>
          </div>
          <div className="flex items-end gap-2 h-20">
            {values.map((v, i) => (
              <div key={i} className="flex flex-col items-center gap-1 flex-1">
                <div
                  className="w-full rounded-t-md bg-blue-500 transition-all"
                  style={{ height: `${(v / maxVal) * 64}px` }}
                />
                <span className="text-xs text-gray-400">{months[i]}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-2">
          {[
            { label: 'Salaires', amount: '-4 200 €', cat: 'Personnel' },
            { label: 'Subvention CAF', amount: '+3 500 €', cat: 'Financement' },
            { label: 'Loyer bureau', amount: '-1 200 €', cat: 'Charges fixes' },
          ].map((t, i) => (
            <div key={i} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
              <div>
                <div className="text-sm font-medium text-gray-800">{t.label}</div>
                <div className="text-xs text-gray-400">{t.cat}</div>
              </div>
              <span className={`text-sm font-bold ${t.amount.startsWith('+') ? 'text-green-600' : 'text-red-600'}`}>
                {t.amount}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function ForecastMockup() {
  const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
  const actual = [58, 52, 61, 55, 63, 58, null, null, null, null, null, null];
  const forecast = [null, null, null, null, null, 58, 54, 67, 72, 65, 78, 82];

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden w-full max-w-xl">
      <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex items-center gap-2">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-red-400" />
          <div className="w-3 h-3 rounded-full bg-yellow-400" />
          <div className="w-3 h-3 rounded-full bg-green-400" />
        </div>
        <span className="text-xs text-gray-500 ml-2 font-medium">TrezoOne — Prévision 12 mois</span>
      </div>
      <div className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-sm font-semibold text-gray-700">Projection de trésorerie</div>
            <div className="text-xs text-gray-400">Exercice 2025</div>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <span className="flex items-center gap-1"><span className="w-3 h-2 rounded bg-blue-500 inline-block" /> Réel</span>
            <span className="flex items-center gap-1"><span className="w-3 h-2 rounded bg-blue-200 inline-block border border-dashed border-blue-400" /> Prévision</span>
          </div>
        </div>
        <div className="flex items-end gap-1 h-24 mb-3">
          {months.map((m, i) => {
            const val = actual[i] ?? forecast[i];
            const isActual = actual[i] !== null;
            return (
              <div key={i} className="flex flex-col items-center gap-0.5 flex-1">
                <div
                  className={`w-full rounded-t transition-all ${isActual ? 'bg-blue-500' : 'bg-blue-200 border border-dashed border-blue-400'}`}
                  style={{ height: `${((val ?? 0) / 82) * 80}px` }}
                />
                <span className="text-[9px] text-gray-400 leading-none">{m}</span>
              </div>
            );
          })}
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-gray-50 rounded-lg p-2 text-center">
            <div className="text-xs text-gray-500">Solde min.</div>
            <div className="font-bold text-sm text-gray-800">52 k€</div>
          </div>
          <div className="bg-blue-50 rounded-lg p-2 text-center">
            <div className="text-xs text-blue-600">Solde moy.</div>
            <div className="font-bold text-sm text-blue-700">64 k€</div>
          </div>
          <div className="bg-green-50 rounded-lg p-2 text-center">
            <div className="text-xs text-green-600">Solde max.</div>
            <div className="font-bold text-sm text-green-700">82 k€</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function StressTestMockup() {
  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden w-full max-w-xl">
      <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex items-center gap-2">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-red-400" />
          <div className="w-3 h-3 rounded-full bg-yellow-400" />
          <div className="w-3 h-3 rounded-full bg-green-400" />
        </div>
        <span className="text-xs text-gray-500 ml-2 font-medium">TrezoOne — Stress Test</span>
      </div>
      <div className="p-5">
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-4 flex items-start gap-3">
          <AlertTriangle className="text-orange-500 flex-shrink-0 mt-0.5" size={18} />
          <div>
            <div className="text-sm font-semibold text-orange-800">Scénario : Retard de subvention</div>
            <div className="text-xs text-orange-600 mt-0.5">Retard de 3 mois sur la subvention principale (15 000 €)</div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-red-50 rounded-xl p-3">
            <div className="text-xs text-red-600 font-medium mb-1">Temps de survie</div>
            <div className="text-2xl font-black text-red-700">47 jours</div>
          </div>
          <div className="bg-orange-50 rounded-xl p-3">
            <div className="text-xs text-orange-600 font-medium mb-1">Date de tension</div>
            <div className="text-lg font-black text-orange-700">15 fév.</div>
          </div>
          <div className="bg-gray-50 rounded-xl p-3">
            <div className="text-xs text-gray-500 font-medium mb-1">Burn Rate</div>
            <div className="text-lg font-black text-gray-700">1 240 €/j</div>
          </div>
          <div className="bg-blue-50 rounded-xl p-3">
            <div className="text-xs text-blue-600 font-medium mb-1">FRD Requis</div>
            <div className="text-lg font-black text-blue-700">3 720 €</div>
          </div>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
            <span>Zone de sécurité</span>
            <span className="font-semibold text-orange-600">Attention</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2">
            <div className="bg-gradient-to-r from-red-400 to-orange-400 h-2 rounded-full" style={{ width: '38%' }} />
          </div>
        </div>
      </div>
    </div>
  );
}

export function SubventionsMockup() {
  const items = [
    { nom: 'Subvention DRAC', montant: 18000, recu: 18000, statut: 'recu' },
    { nom: 'CAF – projet jeunesse', montant: 12000, recu: 8000, statut: 'partiel' },
    { nom: 'Région – fonctionnement', montant: 9500, recu: 0, statut: 'attente' },
    { nom: 'Ville – événementiel', montant: 5000, recu: 0, statut: 'retard' },
  ];

  const colorMap: Record<string, string> = {
    recu: 'bg-green-100 text-green-700',
    partiel: 'bg-blue-100 text-blue-700',
    attente: 'bg-gray-100 text-gray-600',
    retard: 'bg-red-100 text-red-700',
  };
  const labelMap: Record<string, string> = {
    recu: 'Reçu', partiel: 'Partiel', attente: 'En attente', retard: 'Retard',
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden w-full max-w-xl">
      <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex items-center gap-2">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-red-400" />
          <div className="w-3 h-3 rounded-full bg-yellow-400" />
          <div className="w-3 h-3 rounded-full bg-green-400" />
        </div>
        <span className="text-xs text-gray-500 ml-2 font-medium">TrezoOne — Suivi des subventions</span>
      </div>
      <div className="p-5">
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="bg-green-50 rounded-xl p-3 text-center">
            <div className="text-xs text-green-600 font-medium">Reçu</div>
            <div className="text-lg font-black text-green-700">26 000 €</div>
          </div>
          <div className="bg-blue-50 rounded-xl p-3 text-center">
            <div className="text-xs text-blue-600 font-medium">Attendu</div>
            <div className="text-lg font-black text-blue-700">18 500 €</div>
          </div>
          <div className="bg-red-50 rounded-xl p-3 text-center">
            <div className="text-xs text-red-600 font-medium">En retard</div>
            <div className="text-lg font-black text-red-700">5 000 €</div>
          </div>
        </div>
        <div className="space-y-2">
          {items.map((item, i) => (
            <div key={i} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-800 truncate">{item.nom}</div>
                <div className="text-xs text-gray-400">{item.montant.toLocaleString('fr-FR')} €</div>
              </div>
              <div className="flex items-center gap-2 ml-3">
                {item.recu > 0 && item.recu < item.montant && (
                  <span className="text-xs text-gray-500">{item.recu.toLocaleString('fr-FR')} €</span>
                )}
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${colorMap[item.statut]}`}>
                  {labelMap[item.statut]}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function AlertesMockup() {
  const alerts = [
    { type: 'danger', icon: AlertTriangle, title: 'Solde bas détecté', desc: 'Trésorerie sous le seuil de 5 000 €', time: 'Il y a 2h' },
    { type: 'warning', icon: Bell, title: 'Subvention en retard', desc: 'CAF – paiement attendu il y a 15 jours', time: 'Aujourd\'hui' },
    { type: 'info', icon: CheckCircle, title: 'Virement reçu', desc: 'Subvention DRAC : +18 000 € crédités', time: 'Hier' },
  ];

  const colorMap: Record<string, string> = {
    danger: 'border-red-200 bg-red-50',
    warning: 'border-orange-200 bg-orange-50',
    info: 'border-green-200 bg-green-50',
  };
  const iconColorMap: Record<string, string> = {
    danger: 'text-red-500', warning: 'text-orange-500', info: 'text-green-500',
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden w-full max-w-xl">
      <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex items-center gap-2">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-red-400" />
          <div className="w-3 h-3 rounded-full bg-yellow-400" />
          <div className="w-3 h-3 rounded-full bg-green-400" />
        </div>
        <span className="text-xs text-gray-500 ml-2 font-medium">TrezoOne — Alertes</span>
        <span className="ml-auto bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full font-bold">2</span>
      </div>
      <div className="p-5">
        <div className="mb-4">
          <div className="text-sm font-semibold text-gray-700 mb-1">Seuil d'alerte</div>
          <div className="flex items-center gap-3">
            <div className="flex-1 bg-gray-100 rounded-full h-2 relative">
              <div className="bg-red-500 h-2 rounded-full" style={{ width: '18%' }} />
              <div className="absolute top-1/2 -translate-y-1/2 bg-orange-400 w-1.5 h-4 rounded-sm" style={{ left: '25%' }} />
            </div>
            <span className="text-xs text-gray-500">4 200 € / 5 000 €</span>
          </div>
        </div>
        <div className="space-y-2.5">
          {alerts.map((a, i) => {
            const Icon = a.icon;
            return (
              <div key={i} className={`border rounded-xl p-3 flex items-start gap-3 ${colorMap[a.type]}`}>
                <Icon size={16} className={`flex-shrink-0 mt-0.5 ${iconColorMap[a.type]}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-800">{a.title}</div>
                  <div className="text-xs text-gray-500">{a.desc}</div>
                </div>
                <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">{a.time}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function ReportingMockup() {
  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden w-full max-w-xl">
      <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-400" />
            <div className="w-3 h-3 rounded-full bg-yellow-400" />
            <div className="w-3 h-3 rounded-full bg-green-400" />
          </div>
          <span className="text-xs text-gray-500 ml-2 font-medium">TrezoOne — Rapport mensuel</span>
        </div>
        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">Décembre 2024</span>
      </div>
      <div className="p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
            <CheckCircle className="text-green-600" size={22} />
          </div>
          <div>
            <div className="text-sm font-semibold text-gray-800">Situation financière</div>
            <div className="text-xs text-green-600 font-bold">Bonne santé — Score 84/100</div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 mb-4">
          {[
            { label: 'Recettes', value: '+12 800 €', color: 'text-green-700', bg: 'bg-green-50' },
            { label: 'Dépenses', value: '-8 340 €', color: 'text-red-700', bg: 'bg-red-50' },
            { label: 'Résultat', value: '+4 460 €', color: 'text-blue-700', bg: 'bg-blue-50' },
            { label: 'Trésorerie', value: '58 420 €', color: 'text-gray-800', bg: 'bg-gray-50' },
          ].map((kpi, i) => (
            <div key={i} className={`${kpi.bg} rounded-xl p-3`}>
              <div className="text-xs text-gray-500 font-medium">{kpi.label}</div>
              <div className={`text-base font-black ${kpi.color}`}>{kpi.value}</div>
            </div>
          ))}
        </div>
        <div className="border border-gray-100 rounded-xl p-3">
          <div className="text-xs font-semibold text-gray-600 mb-2">Top charges du mois</div>
          {[
            { name: 'Salaires', pct: 78, amount: '6 500 €' },
            { name: 'Loyer', pct: 38, amount: '3 200 €' },
            { name: 'Prestataires', pct: 22, amount: '1 840 €' },
          ].map((c, i) => (
            <div key={i} className="flex items-center gap-2 mb-1.5 last:mb-0">
              <span className="text-xs text-gray-500 w-20 truncate">{c.name}</span>
              <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                <div className="bg-blue-400 h-1.5 rounded-full" style={{ width: `${c.pct}%` }} />
              </div>
              <span className="text-xs font-medium text-gray-700 w-14 text-right">{c.amount}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function KpiMockup() {
  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden w-full max-w-xl">
      <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex items-center gap-2">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-red-400" />
          <div className="w-3 h-3 rounded-full bg-yellow-400" />
          <div className="w-3 h-3 rounded-full bg-green-400" />
        </div>
        <span className="text-xs text-gray-500 ml-2 font-medium">TrezoOne — Vue synthétique</span>
      </div>
      <div className="p-5">
        <div className="grid grid-cols-2 gap-3 mb-4">
          {[
            { label: 'Trésorerie dispo.', value: '58 420 €', delta: '+2 340 €', up: true, color: 'blue' },
            { label: 'Résultat mois', value: '+4 460 €', delta: '+18%', up: true, color: 'green' },
            { label: 'Temps survie', value: '47 jours', delta: 'stable', up: null, color: 'gray' },
            { label: 'Burn rate', value: '1 240 €/j', delta: '-4%', up: false, color: 'orange' },
          ].map((kpi, i) => (
            <div key={i} className="border border-gray-100 rounded-xl p-3">
              <div className="text-xs text-gray-500 font-medium mb-1">{kpi.label}</div>
              <div className="text-lg font-black text-gray-800">{kpi.value}</div>
              <div className={`text-xs flex items-center gap-0.5 mt-1 ${kpi.up === true ? 'text-green-600' : kpi.up === false ? 'text-red-500' : 'text-gray-400'}`}>
                {kpi.up === true && <TrendingUp size={10} />}
                {kpi.up === false && <TrendingDown size={10} />}
                {kpi.delta}
              </div>
            </div>
          ))}
        </div>
        <div className="bg-blue-50 rounded-xl p-3 flex items-center justify-between">
          <div>
            <div className="text-xs text-blue-600 font-semibold">Score de pilotage</div>
            <div className="text-2xl font-black text-blue-700">84 / 100</div>
          </div>
          <div className="w-16 h-16 relative">
            <svg viewBox="0 0 36 36" className="rotate-[-90deg] w-full h-full">
              <circle cx="18" cy="18" r="15.9" fill="none" stroke="#dbeafe" strokeWidth="3" />
              <circle cx="18" cy="18" r="15.9" fill="none" stroke="#2563eb" strokeWidth="3"
                strokeDasharray={`${84} ${100 - 84}`} strokeLinecap="round" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}
